import { LanguageCode } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import React, { useCallback } from 'react';

import SIAPromptContainer from 'Components/structural/SIAPromptContainer/SIAPromptContainer';
import { useAppDispatch } from 'Libs/redux/store';
import { redirect } from 'Libs/routing/redirect';
import ExitAIGeneration from 'Pages/Courses/Edition/components/ExitAIGeneration/ExitAIGeneration';
import { WAIT_TIME_ON_FINISH } from 'Pages/Courses/Edition/views/AILoading/AILoading';
import { displayAlertParams } from 'Pages/Courses/Edition/views/ViewManager/ViewManager';
import { CourseEdited, fetchDraftCourse, generateChapters } from 'Pages/Courses/redux';

import styles from './AIInitial.style';

type AICourseInitialProps = {
  course?: CourseEdited;
  displayAlert: (params: displayAlertParams) => void;
};

export enum AI_MODELS {
  GPT_3_5 = 'GPT_3_5',
  GPT_4 = 'GPT_4', // USED BY DEFAULT
  // CLAUDE_3_5_SONNET = 'CLAUDE_3_5_SONNET', // Not supported by the AI API
  // COHERE_COMMAND_R = 'COHERE_COMMAND_R', // Not supported by the AI API
  // GEMINI_PRO = 'GEMINI_PRO', // Not supported by the AI API
  MISTRAL_SMALL = 'MISTRAL_SMALL',
  MISTRAL_NEMO = 'MISTRAL_NEMO',
  MISTRAL_LARGE = 'MISTRAL_LARGE',
}

export const AICourseInitial: React.FC<AICourseInitialProps> = ({ course, displayAlert }) => {
  const dispatch = useAppDispatch();

  if (!course || !course.id) {
    displayAlert({
      title: t('courses:errors.error'),
      text: t('courses:errors.course_initialization_error'),
      status: 'error',
      icon: 'error',
    });
    redirect('/courses');
    return null;
  }

  const handleStartGeneration = useCallback(
    async (promptText: string, model: AI_MODELS = AI_MODELS.GPT_4) => {
      try {
        const DEFAULT_LANGUAGE = 'fr-FR';
        await dispatch(
          generateChapters({
            language: (course.language as LanguageCode) || DEFAULT_LANGUAGE,
            query: promptText,
            model: model,
            courseId: course.id,
          }),
        );
        setTimeout(() => {
          // This has to be delayed to be triggered after the AILoading page animations have finished
          dispatch(fetchDraftCourse(course.id));
        }, WAIT_TIME_ON_FINISH);
      } catch (error) {
        displayAlert({
          title: t('courses:errors.error'),
          text: t('courses:errors.course_generation_error'),
          status: 'error',
          icon: 'error',
        });
      }
    },
    [dispatch, course.id],
  );

  const initialPrompt = typeof course.aiPrompt === 'string' ? course.aiPrompt : undefined;

  return (
    <div style={styles.wrapper}>
      <div style={styles.promptContainer}>
        <SIAPromptContainer onClickActionButtonHandler={handleStartGeneration} initialPrompt={initialPrompt} />
      </div>

      <ExitAIGeneration />
    </div>
  );
};

export default AICourseInitial;
